import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { StationApi } from 'api/StationApi'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import StationOnMap from 'components/StationOnMap/StationOnMap'
import Title from 'components/Title/Title'
import { useIsMobile } from 'hooks/useIsMobile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { StationActions } from 'store/actions/station'
import { selectStation } from 'store/selectors/station'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'
import { TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { IMapboxElement } from 'utils/interfaces'
import { GEO_MAP_LEFT_PANEL_INSET_PX } from 'utils/scenarioMapLayout'

import StationLeftBlock from './StationLeftBlock/StationLeftBlock'

import style from './StationView.module.css'

const StationView = () => {
  const { t } = useTranslation()
  const station = useSelector(selectStation)
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<ReturnType<IMapboxElement['getMap']> | undefined>()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [showSidebar, setShowSidebar] = useState(true)
  const mapPanelOffset = isMobile ? 0 : GEO_MAP_LEFT_PANEL_INSET_PX

  useEffect(() => {
    // Lots: once on open via stationThunks.fetchStation (not polled).
    const interval = setInterval(async () => {
      if (station?.id) {
        const newStation = await StationApi.fetchStation(station.id)
        dispatch(StationActions.setStation(newStation))
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => clearInterval(interval)
  }, [dispatch, station?.id])

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) {
      setMap(m)
    }
  }

  return (
    <div className={clsx(style.container)}>
      <div className={style.main}>
        <div className={style.titleBlock}>
          <div className={style.titleInner}>
            <div className={style.titleContent}>
              <div className={style.nameRow}>
                <div className={style.titleWithFavorite}>
                  <Title text={station.name || ''} className={style.mobileTitle} />
                  {station.id > 0 && <FavoriteStar kind="station" id={station.id} />}
                </div>
                {!isMobile && (
                  <div className={style.titleId}>
                    {t('robot.serial_number')}: {station.station_id}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={clsx(style.leftBlock, { [style.hidden]: isMobile && !showSidebar })}>
          <Box className={style.box}>
            <StationLeftBlock />
          </Box>
        </div>
        <div className={style.mapWrapper}>
          <MapboxCommon
            ref={mapRef}
            className={style.map}
            style={{ borderRadius: 0 }}
            panelOffset={mapPanelOffset}
            hideSearch
            extraMobileButtonsCount={1}
            onMapInited={onMapInited}
          >
            <StationOnMap map={map ?? mapRef.current?.getMap()} offset={mapPanelOffset} />
          </MapboxCommon>
        </div>
        {isMobile && (
          <button
            className={MapTableToggleStyle.toggle}
            onClick={() => setShowSidebar(prev => !prev)}
            title={showSidebar ? 'Switch to Map' : 'Switch to Panel'}
            style={{
              position: 'absolute',
              bottom: '24px',
              right: '24px',
            }}
          >
            {showSidebar ? (
              <MapIcon className={MapTableToggleStyle.icon} />
            ) : (
              <ListAltIcon className={MapTableToggleStyle.icon} />
            )}
          </button>
        )}
      </div>
    </div>
  )
}

export default StationView
